import React from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  LinkIcon,
} from '@heroicons/react/20/solid';

interface EventDetails {
  topic?: string;
  day?: string;
  time?: string;
  webinarLink?: string;
  description?: string;
  speaker?: string;
  linkedIn?: string;
  date?: string;
}

interface EventCardProps {
  eventDetails?: EventDetails;
}

const EventCard: React.FC<EventCardProps> = ({ eventDetails }) => {
  const handleJoinWebinar = () => {
    if (eventDetails?.webinarLink) {
      window.open(eventDetails.webinarLink, '_blank');
    }
  };

  const handleLinkedInClick = () => {
    const linkedInUrl = eventDetails?.linkedIn;
    if (linkedInUrl) {
      window.open(linkedInUrl, '_blank');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-primary-500 p-6 h-full flex flex-col shadow-lg">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2 text-primary-500">
          Upcoming Event Details
        </h2>
        <div className="w-12 h-0.5 rounded bg-primary-500"></div>
      </div>

      {/* Event Details */}
      <div className="flex-1 space-y-4">
        {eventDetails ? (
          <>
            {/* Topic */}
            {eventDetails.topic && (
              <div>
                <label className="block text-sm font-medium mb-1 text-secondary-900">
                  Topic:
                </label>
                <p className="text-lg font-semibold text-primary-500">
                  {eventDetails.topic}
                </p>
              </div>
            )}

            {/* Day & Date */}
            {(eventDetails.day || eventDetails.date) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {eventDetails.day && (
                  <div>
                    <label className="block text-sm font-medium mb-1 text-secondary-900">
                      Day:
                    </label>
                    <div className="flex items-center text-secondary-700">
                      <CalendarDaysIcon className="w-4 h-4 mr-2 text-primary-500" />
                      <span>{eventDetails.day}</span>
                    </div>
                  </div>
                )}

                {eventDetails.date && (
                  <div>
                    <label className="block text-sm font-medium mb-1 text-secondary-900">
                      Date:
                    </label>
                    <p className="text-secondary-700">{eventDetails.date}</p>
                  </div>
                )}
              </div>
            )}

            {/* Time */}
            {eventDetails.time && (
              <div>
                <label className="block text-sm font-medium mb-1 text-secondary-900">
                  Time:
                </label>
                <div className="flex items-center text-secondary-700">
                  <ClockIcon className="w-4 h-4 mr-2 text-primary-500" />
                  <span>{eventDetails.time}</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-lg text-primary-500">
              No upcoming events scheduled
            </p>
            <p className="text-sm text-secondary-700 mt-2">
              Check back soon for new events!
            </p>
          </div>
        )}

        {eventDetails && (
          <>
            {/* Speaker */}
            {eventDetails.speaker && (
              <div>
                <label className="block text-sm font-medium mb-1 text-secondary-900">
                  Speaker:
                </label>
                <p className="font-medium text-primary-500 mb-2">
                  {eventDetails.speaker}
                </p>
                {eventDetails.linkedIn && (
                  <button
                    onClick={handleLinkedInClick}
                    className="inline-flex items-center text-xs text-primary-500 hover:opacity-80 transition-opacity duration-200 cursor-pointer underline"
                    title="View LinkedIn Profile"
                  >
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    View LinkedIn Profile
                  </button>
                )}
              </div>
            )}

            {/* Description */}
            {eventDetails.description && (
              <div>
                <label className="block text-sm font-medium mb-1 text-secondary-900">
                  Description:
                </label>
                <p className="text-sm leading-relaxed text-secondary-700">
                  {eventDetails.description}
                </p>
              </div>
            )}

            {/* Webinar Link */}
            <div>
              <label className="block text-sm font-medium mb-2 text-secondary-900">
                Webinar Link:
              </label>
              {eventDetails.webinarLink ? (
                <button
                  onClick={handleJoinWebinar}
                  className="inline-flex items-center px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all duration-200 cursor-pointer hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 bg-primary-500"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Join Webinar
                </button>
              ) : (
                <p className="text-sm italic text-secondary-700">
                  Link will be shared soon
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-secondary-50">
        <p className="text-xs text-center text-secondary-700">
          Don't miss out! Mark your calendar and join us for this exciting
          event.
        </p>
      </div>
    </div>
  );
};

export default EventCard;
